import { ModelSetTransformer, ModelTransformer } from '../common/common.model';

export interface ExtMbnaCompliancePopupCategories {
    categoryID: number;
    categoryName: string;
    score: number;
    oppItems: number;
    scoreThresholdColour: string;
    scoreTrendImage: string;
    percentOfTeam: number;
}

export interface ExtMbnaCompliancePopupItem {
    typeID: number;
    pillarID: number;
    pillarName: string;
    categoryID: number;
    categoryName: string;
    userID: number;
    eventDate: string;
    name: string;
    populationType: number;
    populationTypeName: string;
    populationID: number;
    populationName: string;
    periodTypeID: number;
    periodName: string;
    oppItems: number;
    score: number;
    scoreThresholdColour: string;
    scoreTrendImage: string;
    percentOfTeam: number;
    detail?: ExtMbnaCompliancePopupCategories[];
}

export interface MbnaCompliancePopupValue {
    value: number;
    oppItems: number;
    thresholdColor: string;
    trendClass: string;
}

export interface MbnaCompliancePopupCategory {
    categoryID: number;
    categoryName: string;
    value: MbnaCompliancePopupValue;
}

export interface MbnaCompliancePopupUser {
    id: number;
    username: string;
    populationId: number;
    populationName: string;
    populationType: number;
    percentOfTeam: number;
    categories: MbnaCompliancePopupCategoriesIdValue;
}

export interface MbnaCompliancePopupCategoriesIdValue {
    [id: number]: MbnaCompliancePopupCategory;
}

export interface MbnaCompliancePopupItem {
    name: string;
    user: MbnaCompliancePopupUser;
    team: MbnaCompliancePopupUser[];
    overall: MbnaCompliancePopupUser;
}

interface IntMbnaCompliancePopupPillar {
    items: ExtMbnaCompliancePopupItem[];
}

export interface MbnaCompliancePopup {
    items: MbnaCompliancePopupItem[];
}

export interface MbnaCompliancePopUpColumn {
    id: number;
    isOverall: boolean;
    title: string;
}

class MbnaCompliancePopupValueModelTransformer
    implements
        ModelTransformer<
            MbnaCompliancePopupCategory,
            ExtMbnaCompliancePopupItem
        >
{
    transform(
        model: ExtMbnaCompliancePopupCategories,
    ): MbnaCompliancePopupCategory {
        return {
            categoryID: model.categoryID,
            categoryName: model.categoryName,
            value: {
                value: model.score,
                oppItems: model.oppItems,
                thresholdColor: model.scoreThresholdColour,
                trendClass: model.scoreTrendImage.toLowerCase(),
            },
        };
    }
}

class MbnaCompliancePopupUserModelTransformer
    implements
        ModelTransformer<MbnaCompliancePopupUser, ExtMbnaCompliancePopupItem>
{
    transform(model: ExtMbnaCompliancePopupItem): MbnaCompliancePopupUser {
        return {
            id: model.userID,
            username: model.name,
            populationId: model.populationID,
            populationName: model.populationName,
            populationType: model.populationType,
            percentOfTeam:
                model.percentOfTeam == null ? null : model.percentOfTeam / 100,
            categories: model.detail.reduce((p, c) => {
                p[c.categoryID] =
                    new MbnaCompliancePopupValueModelTransformer().transform(c);
                return p;
            }, {}),
        };
    }
}

class MbnaCompliancePopupUserModelSetTransformer extends ModelSetTransformer<
    MbnaCompliancePopupUser,
    ExtMbnaCompliancePopupItem
> {
    protected newTransformer(): ModelTransformer<
        MbnaCompliancePopupUser,
        ExtMbnaCompliancePopupItem
    > {
        return new MbnaCompliancePopupUserModelTransformer();
    }
}

class MbnaCompliancePopupItemModelTransformer
    implements
        ModelTransformer<MbnaCompliancePopupItem, IntMbnaCompliancePopupPillar>
{
    transform(model: IntMbnaCompliancePopupPillar): MbnaCompliancePopupItem {
        const users_ = model.items.filter((value) => value.typeID !== 1);
        const grouped = groupData(users_);
        const users =
            new MbnaCompliancePopupUserModelSetTransformer().transform(grouped);
        const overall_ = model.items.filter((value) => value.typeID === 1);
        let overall;
        if (overall_) {
            overall = groupData(overall_).map((o) =>
                new MbnaCompliancePopupUserModelTransformer().transform(o),
            )[0];
        }
        const output = {
            name: model.items[0].pillarName,
            user: users.length !== 0 ? users[0] : null,
            team: users
                .slice(1)
                .sort((p, c) => (p.username < c.username ? -1 : 1)),
            overall,
        };
        console.log(output);
        return output;
    }
}

class MbnaCompliancePopupItemModelSetTransformer extends ModelSetTransformer<
    MbnaCompliancePopupItem,
    IntMbnaCompliancePopupPillar
> {
    protected newTransformer(): ModelTransformer<
        MbnaCompliancePopupItem,
        IntMbnaCompliancePopupPillar
    > {
        return new MbnaCompliancePopupItemModelTransformer();
    }
}

export class MbnaCompliancePopupModelTransformer
    implements
        ModelTransformer<MbnaCompliancePopup, ExtMbnaCompliancePopupItem[]>
{
    transform(model: ExtMbnaCompliancePopupItem[]): MbnaCompliancePopup {
        const pillarsIds = [];
        const pillars_: IntMbnaCompliancePopupPillar[] = [];
        model.forEach((value) => {
            const index = pillarsIds.indexOf(value.pillarID);
            if (index !== -1) {
                pillars_[index].items.push(value);
                return;
            }
            pillarsIds.push(value.pillarID);
            pillars_.push({
                items: [value],
            });
        });
        const items =
            new MbnaCompliancePopupItemModelSetTransformer().transform(
                pillars_,
            );
        return {
            items,
        };
    }
}

export function validateMbnaCompliancePopup(model: MbnaCompliancePopup): void {
    // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
    const valid =
        // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
        model != null && model.items != null && model.items.length !== 0;
    if (!valid) {
        throw new Error('MbnaCompliancePopup not valid');
    }
}

function groupData(
    data: ExtMbnaCompliancePopupItem[],
): ExtMbnaCompliancePopupItem[] {
    return groupBy(
        data,
        (item) => [item.eventDate, item.userID, item.populationID, item.name],
        (group) => {
            return group.reduce((p, c, index) => {
                p.detail = p.detail || [
                    {
                        categoryID: p.categoryID,
                        categoryName: p.categoryName,
                        score: p.score,
                        oppItems: p.oppItems,
                        scoreThresholdColour: p.scoreThresholdColour,
                        scoreTrendImage: p.scoreTrendImage,
                        percentOfTeam: p.percentOfTeam,
                    },
                ];
                // eslint-disable-next-line eqeqeq
                if (index == 0) return p;
                p.detail.push({
                    categoryID: c.categoryID,
                    categoryName: c.categoryName,
                    score: c.score,
                    oppItems: c.oppItems,
                    scoreThresholdColour: c.scoreThresholdColour,
                    scoreTrendImage: c.scoreTrendImage,
                    percentOfTeam: c.percentOfTeam,
                });
                return p;
            }, group[0]);
        },
    ).map((g) => {
        g.detail = g.detail.sort((p, c) =>
            p.categoryID < c.categoryID ? -1 : 1,
        );
        return g;
    });
}

function groupBy<T>(
    array: T[],
    f: (item: T) => Array<string | number>,
    output?: (obj: T[]) => T,
): T[] {
    const groups = {};
    array.forEach(function (o) {
        const group = JSON.stringify(f(o));
        groups[group] = groups[group] || [];
        groups[group].push(o);
    });

    return Object.keys(groups).map(function (group) {
        if (output) {
            return output(groups[group]);
        }
        return groups[group];
    });
}
